import {ComboboxProvider, PopoverDisclosure, PopoverProvider, usePopoverStore, useStoreState} from '@ariakit/react'
import {ReactElement} from 'react'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useTranslation} from '@/translations/i18n'
import {SelectVirtualList} from '@/components/ui/select-atoms/SelectAtoms'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'

import {
    StyledUsersAndGroupsMultiSelectComboboxList,
    StyledUsersAndGroupsMultiSelectOverlay,
    StyledUsersAndGroupsMultiSelectPopover,
    StyledUsersAndGroupsMultiSelectPopoverInput,
    StyledUsersAndGroupsMultiSelectPopoverOption,
    StyledUsersAndGroupsSelectGroupLabel
} from './style'
import {useUsersAndGroupsSearchableList, CombinedItem} from '@/features/users/hooks/useUsersAndGroupsSearchableList'
import {User01Icon, Users01Icon} from '@/components/ui/icon/Icon'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'

export interface SelectedItem {
    type: 'user' | 'group'
    id: string
}

export interface SelectedItemWithData extends SelectedItem {
    id: string
    type: 'user' | 'group'
    name: string
    avatarUrl?: string | null
}

// New interface for the callback that always receives users
export interface UserSelectionItem {
    type: 'user'
    id: string
}

interface UsersAndGroupsMultiSelectPopoverProps {
    trigger: ReactElement
    toggleClick: (users: UserSelectionItem[], itemData: CombinedItem) => void
    selectedItems: SelectedItem[]
    onClose?: () => void
    currentDisplayItems?: SelectedItemWithData[]
}

export const UsersAndGroupsMultiSelectPopover = ({
    trigger,
    toggleClick,
    selectedItems,
    onClose,
    currentDisplayItems = []
}: UsersAndGroupsMultiSelectPopoverProps) => {
    const {t} = useTranslation()
    const popoverStore = usePopoverStore({
        placement: 'bottom-start'
    })
    const isOpen = useStoreState(popoverStore, 'open')
    const dataQuery = useUsersAndGroupsSearchableList({
        limit: 10
    })

    const handlePopoverOpenChange = (open: boolean) => {
        // Only close if explicitly requested, not on every interaction
        if (!open && onClose) {
            onClose()
        }
    }

    const handleToggleClick = (item: CombinedItem, event: React.MouseEvent) => {
        // Prevent all default behaviors and propagation
        event.preventDefault()
        event.stopPropagation()

        // Skip if it's a section header
        if (item.type === 'section-header') return

        if (item.type === 'group') {
            // When selecting a group, pass all its members as individual users
            const groupMembers: UserSelectionItem[] =
                item.members?.map(member => ({
                    type: 'user' as const,
                    id: member.id
                })) || []

            toggleClick(groupMembers, item)
        } else if (item.type === 'user') {
            // When selecting a user, pass it as a single user
            const userSelection: UserSelectionItem[] = [
                {
                    type: 'user' as const,
                    id: item.id
                }
            ]

            toggleClick(userSelection, item)
        }
    }

    return (
        <PopoverProvider store={popoverStore} setOpen={handlePopoverOpenChange}>
            <PopoverDisclosure render={trigger} />
            {isOpen && <StyledUsersAndGroupsMultiSelectOverlay onClick={() => popoverStore.setOpen(false)} />}
            <StyledUsersAndGroupsMultiSelectPopover gutter={8} onClick={e => e.stopPropagation()}>
                <ComboboxProvider open setValue={dataQuery.onSearch} resetValueOnHide>
                    <StyledUsersAndGroupsMultiSelectPopoverInput
                        hasCloseCombobox
                        placeholder={t('commons:search_placeholder')}
                    />
                    <StyledUsersAndGroupsMultiSelectComboboxList>
                        {dataQuery?.isLoading ? (
                            <>
                                {Array.from({length: 6}, (_, index) => (
                                    <Skeleton key={index} height={32} />
                                ))}
                            </>
                        ) : dataQuery.isError ? (
                            <InputHelpText error={t('errors:default')} />
                        ) : (
                            <SelectVirtualList
                                data={[...currentDisplayItems, ...dataQuery.remappedData]}
                                totalCount={dataQuery.remappedData.length}
                                overscan={100}
                                style={{height: 200}}
                                endReached={() => dataQuery.hasNextPage && dataQuery.fetchNextPage()}
                                itemContent={(itemIndex, item: CombinedItem) => {
                                    // Check if this is a section header
                                    if (item.type === 'section-header') {
                                        return (
                                            <StyledUsersAndGroupsSelectGroupLabel key={itemIndex}>
                                                {item.name}
                                            </StyledUsersAndGroupsSelectGroupLabel>
                                        )
                                    }

                                    const getCheckboxState = (() => {
                                        if (item.type === 'user') {
                                            // For users, check if the user is directly selected
                                            const isSelected = selectedItems.some(
                                                selected => selected.type === 'user' && selected.id === item.id
                                            )
                                            return {checked: isSelected, indeterminate: false}
                                        } else if (item.type === 'group') {
                                            // For groups, determine state based on member selection
                                            if (!item.members || item.members.length === 0) {
                                                return {checked: false, indeterminate: false}
                                            }

                                            const selectedMembersCount = item.members.filter(member =>
                                                selectedItems.some(
                                                    selected => selected.type === 'user' && selected.id === member.id
                                                )
                                            ).length

                                            const totalMembersCount = item.members.length

                                            if (selectedMembersCount === 0) {
                                                return {checked: false, indeterminate: false}
                                            } else if (selectedMembersCount === totalMembersCount) {
                                                return {checked: true, indeterminate: false}
                                            } else {
                                                return {checked: false, indeterminate: true}
                                            }
                                        }
                                        return {checked: false, indeterminate: false}
                                    })()

                                    return (
                                        <StyledUsersAndGroupsMultiSelectPopoverOption
                                            key={itemIndex}
                                            variant="ghost"
                                            shape="text"
                                            onClick={e => handleToggleClick(item, e)}
                                            onMouseDown={e => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                            }}
                                            onMouseUp={e => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                            }}
                                        >
                                            <Checkbox
                                                id={`${item.type}-${item.id}`}
                                                checked={getCheckboxState.checked}
                                                indeterminate={getCheckboxState.indeterminate}
                                            />
                                            <Flexbox direction="column" align="flex-start">
                                                <Flexbox gap={2} direction="row" align="center">
                                                    {item.type === 'group' ? (
                                                        <Users01Icon size={16} />
                                                    ) : (
                                                        <User01Icon size={16} />
                                                    )}
                                                    <Text size="textSm" color="neutral.900">
                                                        {item.type === 'group'
                                                            ? item.name
                                                            : (item.fullName ?? item.name)}
                                                    </Text>
                                                </Flexbox>
                                                {item.type === 'group' && item.members && (
                                                    <span style={{fontSize: '0.65rem'}}>
                                                        ({item.members.length} {t('commons:members')}:{' '}
                                                        {item.members.length > 10
                                                            ? item.members
                                                                  .slice(0, 10)
                                                                  .map(member => member.fullName)
                                                                  .join(', ') + '...'
                                                            : item.members.map(member => member.fullName).join(', ')}
                                                        )
                                                    </span>
                                                )}
                                            </Flexbox>
                                        </StyledUsersAndGroupsMultiSelectPopoverOption>
                                    )
                                }}
                            />
                        )}
                    </StyledUsersAndGroupsMultiSelectComboboxList>
                </ComboboxProvider>
            </StyledUsersAndGroupsMultiSelectPopover>
        </PopoverProvider>
    )
}
